/**
 * Extra-charge kinds (ZOY-776 / backend ZOY-775, Owner 2026-10-09): the
 * clinic's own list of what reception may charge beside a session — by time
 * (rate per 15 minutes, optionally counting as practitioner working time),
 * fixed with a default, or free. Switched off, never deleted; the pricing
 * model is fixed at creation, the rate and name are not.
 */

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Notice, TextField } from '@/ui/kit';
import { FieldGrid, FormCard, SelectField, Span2, Toggle } from '@/ui/formkit';
import { Column, PrimaryCell, RowButton, WorkTable } from '@/ui/WorkTable';
import { parseNumericInput, groupAmount, plainAmount } from '@/core/i18n/digits';
import { ApiError } from '@/core/api/errors';
import {
  ChargeErrorCode,
  createExtraChargeKind,
  EXTRA_CHARGE_PRICINGS,
  listExtraChargeKinds,
  patchExtraChargeKind,
  setExtraChargeKindActive,
  type ExtraChargeKind,
  type ExtraChargePricing,
} from '@/core/clinic/paymentsApi';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM } from '@/core/authz/permissions';
import { storageUnit } from '@/core/clinic/money';
import { useClinicSettings } from '../patients/useClinicSettings';
import { usePaymentFormatters } from '../payments/paymentShared';

type View = { kind: 'list' } | { kind: 'create' } | { kind: 'edit'; row: ExtraChargeKind };

export function ExtraChargesTab() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id }) as string;
  const authz = useAuthz();
  const manage = authz.can(PERM.settingsManage);
  const queryClient = useQueryClient();
  const f = usePaymentFormatters();
  const [view, setView] = useState<View>({ kind: 'list' });
  const [banner, setBanner] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['extra-charge-kinds', 'all', authz.clinicId],
    queryFn: listExtraChargeKinds,
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['extra-charge-kinds'] });
  const toggle = useMutation({
    mutationFn: (row: ExtraChargeKind) => setExtraChargeKindActive(row.id, !row.active),
    onSuccess: invalidate,
    onError: () => setBanner(t('xk.saveFailed')),
  });

  const columns: Column[] = [
    { id: 'name', label: t('xk.name'), width: '2fr' },
    { id: 'rate', label: t('xk.pricing'), width: '1.4fr', tier: 'tablet-up' },
    { id: 'status', label: t('staff.colStatus'), width: 'auto', tier: 'tablet-up' },
    { id: 'actions', label: t('staff.colActions'), width: 'auto', align: 'end' },
  ];
  const rateOf = (k: ExtraChargeKind) =>
    k.pricing === 'free'
      ? t('xk.pricing.free')
      : `${t(k.pricing === 'time' ? 'xk.unitTime' : 'xk.unitFixed')}: ${f.money(k.unitAmount ?? 0)}`;

  if (view.kind !== 'list') {
    return (
      <KindForm
        row={view.kind === 'edit' ? view.row : null}
        onDone={() => {
          setView({ kind: 'list' });
          invalidate();
        }}
        onCancel={() => setView({ kind: 'list' })}
      />
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxInlineSize: 760 }}>
      <p className={a.pageSub}>{t('xk.sub')}</p>
      {banner && (
        <Notice kind="err" role="alert">
          {banner}
        </Notice>
      )}
      <WorkTable<ExtraChargeKind>
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        error={list.isError}
        onRetry={() => void list.refetch()}
        toolbar={
          manage ? (
            <Button variant="quiet" onClick={() => setView({ kind: 'create' })}>
              {t('xk.add')}
            </Button>
          ) : undefined
        }
        empty={{ title: t('xk.emptyTitle'), body: t('xk.emptyBody') }}
        renderCell={(k, col) => {
          switch (col) {
            case 'name':
              return (
                <PrimaryCell
                  main={<bdi>{k.name}</bdi>}
                  sub={
                    <>
                      {t(`xk.pricing.${k.pricing}`)}
                      {k.countsAsPractitionerTime && <> · {t('xk.counts')}</>}
                    </>
                  }
                />
              );
            case 'rate':
              return <span className="zc-tabular">{rateOf(k)}</span>;
            case 'status':
              return k.active ? (
                <Chip kind="ok">{t('common.enabled')}</Chip>
              ) : (
                <Chip kind="quiet">{t('xk.disabled')}</Chip>
              );
            case 'actions':
              return manage ? (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <RowButton onClick={() => setView({ kind: 'edit', row: k })}>
                    {t('xk.edit')}
                  </RowButton>
                  <RowButton danger={k.active} onClick={() => toggle.mutate(k)}>
                    {k.active ? t('xk.disable') : t('xk.enable')}
                  </RowButton>
                </span>
              ) : null;
            default:
              return null;
          }
        }}
      />
    </div>
  );
}

function KindForm(props: {
  row: ExtraChargeKind | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id }) as string;
  const settings = useClinicSettings();
  const editing = props.row;
  const [name, setName] = useState(editing?.name ?? '');
  const [pricing, setPricing] = useState<ExtraChargePricing>(editing?.pricing ?? 'time');
  const [unit, setUnit] = useState(editing?.unitAmount ? String(Number(editing.unitAmount)) : '');
  const [counts, setCounts] = useState(editing?.countsAsPractitionerTime ?? true);
  const [sortOrder, setSortOrder] = useState(String(editing?.sortOrder ?? 0));
  const [error, setError] = useState<string | null>(null);

  const unitN = parseNumericInput(unit);
  const needsUnit = pricing !== 'free';
  const unitOk = !needsUnit || (unitN !== null && unitN > 0);
  const order = parseNumericInput(sortOrder);
  const ready = name.trim() !== '' && unitOk && order !== null && order >= 0;

  const save = useMutation({
    mutationFn: () =>
      editing
        ? patchExtraChargeKind(editing.id, {
            name: name.trim(),
            ...(needsUnit ? { unitAmount: unitN! } : {}),
            ...(editing.pricing === 'time' ? { countsAsPractitionerTime: counts } : {}),
            sortOrder: Math.round(order!),
          })
        : createExtraChargeKind({
            name: name.trim(),
            pricing,
            ...(needsUnit ? { unitAmount: unitN! } : {}),
            ...(pricing === 'time' ? { countsAsPractitionerTime: counts } : {}),
            sortOrder: Math.round(order!),
          }),
    onSuccess: props.onDone,
    onError: (err) =>
      setError(
        err instanceof ApiError && err.code === ChargeErrorCode.KindUnitAmountInvalid
          ? t('xk.errUnit')
          : t('xk.saveFailed'),
      ),
  });

  return (
    <FormCard
      title={editing ? t('xk.edit') : t('xk.add')}
      sub={editing ? t('xk.pricingLocked') : undefined}
      onSubmit={() => ready && !save.isPending && save.mutate()}
      cta={t('common.save')}
      ctaDisabled={!ready || save.isPending}
      busy={save.isPending}
      secondary={{ label: t('common.cancel'), onClick: props.onCancel }}
    >
      <FieldGrid>
        <Span2>
          <TextField
            label={t('xk.name')}
            value={name}
            onChange={setName}
            requirement={{ word: t('signin.required'), kind: 'required' }}
            autoFocus
          />
        </Span2>
        {editing ? (
          <TextField
            label={t('xk.pricing')}
            value={t(`xk.pricing.${editing.pricing}`)}
            onChange={() => undefined}
            readOnly
          />
        ) : (
          <SelectField
            label={t('xk.pricing')}
            value={pricing}
            onChange={(v) => setPricing(v as ExtraChargePricing)}
            options={EXTRA_CHARGE_PRICINGS.map((p) => ({ value: p, label: t(`xk.pricing.${p}`) }))}
          />
        )}
        {needsUnit && (
          <TextField
            label={t(pricing === 'time' ? 'xk.unitTime' : 'xk.unitFixed')}
            value={groupAmount(unit)}
            onChange={(v) => setUnit(plainAmount(v))}
            error={unit !== '' && !unitOk ? t('xk.errUnit') : undefined}
            requirement={{ word: t('signin.required'), kind: 'required' }}
            machine
            inputMode="decimal"
            prefix={storageUnit(settings?.currencyCode)}
          />
        )}
        {pricing === 'time' && (
          <Span2>
            <Toggle
              label={t('xk.counts')}
              help={t('xk.countsHelp')}
              checked={counts}
              onChange={setCounts}
              stateWord={t(counts ? 'xk.countsOn' : 'xk.countsOff')}
            />
          </Span2>
        )}
        <TextField
          label={t('xk.sortOrder')}
          value={sortOrder}
          onChange={setSortOrder}
          machine
          inputMode="numeric"
        />
        {error && (
          <Span2>
            <Notice kind="err" role="alert">
              {error}
            </Notice>
          </Span2>
        )}
      </FieldGrid>
    </FormCard>
  );
}
